import React, { useState, useEffect } from 'react';
import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Switch, TextField, Typography } from "@mui/material";
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

export const AddMenuForm = ({ catgList = [], menuList, itemInfo = {}, onsave, custmizList = [] }) => {
    const { Logout } = useAuth();

    const initialState = {
        _id: itemInfo._id || null,
        storeID: JSON.parse(window.localStorage.getItem('ds')),
        categoryID: itemInfo.categoryID || '',
        newCatgory: '',
        newItem: itemInfo.item || '',
        price: itemInfo.price || 0,
        decription: itemInfo.description || '',
        available: itemInfo.available ?? true, // Use nullish coalescing for booleans
        inStore: itemInfo.inStore ?? true,
        online: itemInfo.online ?? false,
        restaurantPatner: itemInfo.restaurantPatner ?? false,
        image: itemInfo.image || null,
        custmzid: itemInfo.custmzid || '',

        // --- INVENTORY FIELDS ---
        stockmanaged: itemInfo.stockmanaged ?? false,
        isCombo: itemInfo.isCombo ?? false,
        itemsellcnt: itemInfo.itemsellcnt || 1,
        currentStock: itemInfo.currentStock || 0,
        minThr: itemInfo.minThr || 5,
        maxThr: itemInfo.maxThr || 0,
        pCost: itemInfo.pCost || 0.00,
        supplier: itemInfo.supplier || '',
        unit: itemInfo.unit || 'unit',
        barcode: itemInfo.barcode || '',
        sku: itemInfo.sku || '',
        recipeComponents: itemInfo.recipeComponents || [] // [ { itemID, qty } ]
    };

    const [formData, setFormData] = useState(initialState);
    const [newCatgField, setNewCatgField] = useState('none');
    const [categoryOptions,] = useState(catgList);
    const [menuInvtry, setMenuInventory] = useState([]); // Raw materials for dropdown //
    const [imagePreview, setImagePreview] = useState(null);
    const [showInventory, setShowInventory] = useState(false);


    // --- Data Fetching: Categories & Inventory Items ---
    useEffect(() => {
        async function ExtractMenuItem() {
            var menuInvtList = menuList.filter(item => item.stockmanaged === true);
            setMenuInventory(menuInvtList);
        }
        ExtractMenuItem();
    }, [menuList, Logout]);


    const handleFormSubmit = () => {
        // Validation check for required inventory fields if stock is managed
        if (formData.stockmanaged && (formData.currentStock === null || formData.minThr === null)) {
            alert('Stock management is enabled. Please fill Current Stock and Min Threshold.');
            return;
        }
        // Validation for combos
        if (formData.isCombo && formData.recipeComponents.length === 0) {
            alert('Combo product requires at least one recipe component.');
            return;
        }

        // Handle image conversion
        if (formData.image && typeof formData.image !== 'string') {
            convertToBase64(formData.image).then(base64Image => {
                const updatedFormData = { ...formData, image: base64Image };
                onsave(updatedFormData, itemInfo.currentCatgory);
            });
        } else {
            onsave(formData, itemInfo.currentCatgory);
        }
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        let inputValue = value;

        if (type === 'number') {
            inputValue = value === '' ? null : parseFloat(value);
        } else if (type === 'checkbox' || type === 'switch') {
            inputValue = checked;
        }

        setFormData(prev => ({
            ...prev,
            [name]: inputValue
        }));

        // Handle New Category input field visibility
        if (name === 'categoryID') {
            setNewCatgField(value === '000' ? '' : 'none');
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFormData({ ...formData, image: file });
            setImagePreview(URL.createObjectURL(file));
        }
    };

    // --- Recipe Component Handlers ---
    const handleAddComponent = () => {
        // Add a new component with defaults from the first item in inventory list
        const defaultItem = menuInvtry[0] || { _id: '', unit: 'unit' };

        setFormData(prev => ({
            ...prev,
            recipeComponents: [...prev.recipeComponents, {
                itemID: defaultItem._id,
                qty: 1,
                unit: defaultItem.unit, // Store unit for display
            }]
        }));
    };

    const handleRemoveComponent = (index) => {
        const newComponents = formData.recipeComponents.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, recipeComponents: newComponents }));
    };

    /// Following Will handle component Change in the Recipies...
    const handleComponentChange = (index, field, value) => {
        const newComponents = formData.recipeComponents.map((component, i) => {
            if (i === index) {
                let updatedComponent = { ...component };

                // 1. Handle ITEM ID change (update unit and ID)
                if (field === 'itemID') {
                    const item = menuInvtry.find(inv => inv._id === value);
                    updatedComponent.itemID = value;
                    updatedComponent.unit = item ? item.unit : 'unit'; // Update unit display
                } else {
                    // 2. Handle Quantity change (convert to float/null)
                    const finalValue = field === 'qty' ? (value === '' ? null : parseFloat(value)) : value;
                    updatedComponent[field] = finalValue;
                }
                return updatedComponent;
            }
            return component;
        });
        setFormData(prev => ({ ...prev, recipeComponents: newComponents }));
    };


    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: '1200px' }}>

            {/* Left Column: Core Menu Details */}
            <Box sx={{ flexGrow: 1, minWidth: 350 }}>
                <Paper elevation={3} sx={{ p: 3, backgroundColor: '#f0f4f8' }}>
                    <Typography variant="h6" color="primary.dark" sx={{ mb: 2 }}>Basic Menu Information</Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {/* Category Dropdown */}
                        <FormControl sx={{ m: 1, flexGrow: 1, minWidth: 300 }} variant="outlined" size="small">
                            <TextField
                                select
                                label="Category"
                                name="categoryID"
                                value={formData.categoryID}
                                onChange={handleInputChange}
                            >
                                {categoryOptions.map((option) => (
                                    <MenuItem key={option._id} value={option._id}>
                                        {option.catgory}
                                    </MenuItem>
                                ))}
                                <MenuItem key="new-item" value="000" sx={{ color: "coral" }}>
                                    + New Category
                                </MenuItem>
                            </TextField>
                        </FormControl>

                        {/* New Category Input */}
                        <FormControl sx={{ m: 1, flexGrow: 1, display: newCatgField }} variant="outlined" size="small">
                            <TextField
                                label="Add New Category Name"
                                name="newCatgory"
                                value={formData.newCatgory}
                                onChange={handleInputChange}
                            />
                        </FormControl>

                        {/* Item Name & Price */}
                        <FormControl sx={{ m: 1, width: '60%' }} variant="outlined" size="small">
                            <TextField label="Item / Dish Name" name="newItem" value={formData.newItem} onChange={handleInputChange} required />
                        </FormControl>
                        <FormControl sx={{ m: 1, width: '30%' }} variant="outlined" size="small">
                            <TextField label="Price" name="price" type="number" value={formData.price} onChange={handleInputChange} required
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            />
                        </FormControl>

                        {/* Description */}
                        <FormControl sx={{ m: 1, width: '95%' }} variant="outlined">
                            <TextField label="Description" name="decription" value={formData.decription} onChange={handleInputChange} multiline rows={3} />
                        </FormControl>
                        <FormControl sx={{ m: 1, width: '95%' }} size="small">
                                <InputLabel>Side-Car (Customization)</InputLabel>
                                <Select name="custmzid" value={formData.custmzid} label="Side-Car (Customization)" onChange={handleInputChange}>
                                    <MenuItem value="none">None</MenuItem>
                                    {custmizList.map(item => (
                                        <MenuItem key={item._id} value={item._id}>{String(item.name)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ m: 1 }}>Sales Channels</Typography>
                    <Box sx={{ m: 1, width: '95%', pl: 1 }}>
                        <FormControlLabel name="available" control={<Switch color="primary" checked={formData.available} onChange={handleInputChange} />} label="Available" labelPlacement="start" />
                        <br />
                        <FormControlLabel name="inStore" control={<Checkbox onChange={handleInputChange} checked={formData.inStore} />} label="In-Store" />
                        <FormControlLabel name="online" control={<Checkbox onChange={handleInputChange} checked={formData.online} />} label="Online" />
                        <FormControlLabel name="restaurantPatner" control={<Checkbox onChange={handleInputChange} checked={formData.restaurantPatner} />} label="Restaurant Partner" />
                    </Box>

                    {/* Image Upload Section */}
                    <Box sx={{ m: 1, width: '95%', p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
                        <input accept="image/*" type="file" onChange={handleImageChange} style={{ display: "none" }} id="upload-image" />
                        <label htmlFor="upload-image">
                            <Button variant="outlined" component="span" size="small">Upload Image</Button>
                        </label>
                        {imagePreview && (
                            <Box sx={{ mt: 2 }}>
                                <img src={imagePreview} alt="Preview" width="100" style={{ borderRadius: '4px' }} />
                            </Box>
                        )}
                    </Box>
                    
                </Paper>
            </Box>
            {/* Right Column: Stock Management & Barcode */}
            <Box sx={{ flexGrow: 1, minWidth: 350 }}>
                <Paper elevation={3} sx={{ p: 3, height: '100%', backgroundColor: '#fff3e0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" color="warning.dark">Inventory & SKU</Typography>
                        <Button onClick={() => setShowInventory(!showInventory)} size="small" color="secondary" sx={{ textTransform: 'none' }}>
                            {showInventory ? 'Hide Details' : 'Show More Options'}
                        </Button>
                    </Box>
                    {showInventory && (
                        <>
                            <Box sx={{ display: 'flex', flexDirection: 'Wrap', gap: 2, mt: 2 }}>
                                <FormControl sx={{ width: '30%' }} size="small" required>
                                    <InputLabel>Unit of Measure</InputLabel>
                                    <Select
                                        name="unit"
                                        value={formData.unit}
                                        label="Unit of Measure"
                                        onChange={handleInputChange}
                                    >
                                        {['kg', 'g', 'Liters', 'ml', 'unit', 'pack'].map(unit => (
                                            <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl sx={{ width: '40%' }} variant="outlined">
                                    <TextField
                                        label="SKU / Product Code"
                                        name="sku"
                                        type="text"
                                        size="small"
                                        value={formData.sku}
                                        onChange={handleInputChange}
                                    />
                                </FormControl>
                                <FormControl sx={{ width: '40%' }} variant="outlined">
                                    <TextField
                                        label="Barcode (UPC)"
                                        name="barcode"
                                        type="text"
                                        size="small"
                                        value={formData.barcode}
                                        onChange={handleInputChange}
                                    />
                                </FormControl>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle1">Enable Stock Tracking :</Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.stockmanaged}
                                                onChange={handleInputChange}
                                                name="stockmanaged"
                                                color="primary"
                                            />
                                        }
                                        label={formData.stockmanaged ? "Enable" : "Disable"}
                                        labelPlacement="start"
                                    />
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                {formData.stockmanaged && (
                                    <>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, p: 1 }}>
                                            <FormControl sx={{ width: '30%', minWidth: 200 }} variant="outlined">
                                                <TextField
                                                    label={`Current Stock Quantity (${formData.unit})`}
                                                    name="currentStock"
                                                    type="number"
                                                    size="small"
                                                    value={formData.currentStock}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </FormControl>

                                            <FormControl sx={{ width: '60%', minWidth: 200 }} variant="outlined">
                                                <TextField
                                                    label="Minimum Threshold (Low Stock Alert)"
                                                    name="minThr"
                                                    type="number"
                                                    size="small"
                                                    value={formData.minThr}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </FormControl>
                                            <FormControl sx={{ width: '60%', minWidth: 200 }} variant="outlined">
                                                <TextField
                                                    label="Maximum Stock (Optional)"
                                                    name="maxThr"
                                                    type="number"
                                                    size="small"
                                                    value={formData.maxThr}
                                                    onChange={handleInputChange}
                                                />
                                            </FormControl>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, p: 1 }}>
                                            <FormControl sx={{ width: '60%' }} variant="outlined">
                                                <TextField
                                                    label="Purchase Cost (P/Unit)"
                                                    name="pCost"
                                                    type="number"
                                                    size="small"
                                                    value={formData.pCost}
                                                    onChange={handleInputChange}
                                                />
                                            </FormControl>
                                            <FormControl sx={{ width: '30%' }} variant="outlined">
                                                <TextField
                                                    label="Primary Supplier"
                                                    name="supplier"
                                                    type="text"
                                                    size="small"
                                                    value={formData.supplier}
                                                    onChange={handleInputChange}
                                                />
                                            </FormControl>
                                            <FormControl sx={{ width: '60%' }} variant="outlined">
                                                <TextField
                                                    label="Items Per Sale Unit"
                                                    name="itemsellcnt"
                                                    type="number"
                                                    size="small"
                                                    value={formData.itemsellcnt}
                                                    onChange={handleInputChange}
                                                    helperText="How many units of this item are sold at once (e.g., 1)."
                                                    required
                                                />
                                            </FormControl>
                                        </Box>
                                    </>
                                )}
                                {/* ITEM TYPE TOGGLE (Simple Item vs. Combo/Recipe) */}
                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle1" >Product Type:</Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.isCombo}
                                                onChange={handleInputChange}
                                                name="isCombo"
                                                color="secondary"
                                            />
                                        }
                                        label={formData.isCombo ? "Combo/Recipe Product" : "Simple Stock Item"}
                                    />
                                </Box>
                            </Box>

                            {/* --- Combo/Recipe Configuration (If IS a Combo) --- */}
                            {formData.isCombo && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" color="secondary.main">Recipe Components:</Typography>
                                    <Typography variant="caption" color="text.secondary">Define which inventory items are consumed when ONE unit of **"{formData.newItem}"** is sold.</Typography>
                                    {formData.recipeComponents.map((component, index) => (
                                        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', backgroundColor: '#f9f9f9', p: 1, borderRadius: 1 }}>
                                            {/* Quantity Consumed */}
                                            <TextField
                                                label={`Qty`}
                                                name="quantityConsumed"
                                                type="number"
                                                size="small"
                                                value={component.qty}
                                                onChange={(e) => handleComponentChange(index, 'qty', e.target.value)}
                                                sx={{ width: 100 }}
                                            />
                                            {/* Inventory Item Selector */}
                                            <FormControl size="small" sx={{ flexGrow: 1 }}>
                                                <InputLabel>Inventory Item</InputLabel>
                                                <Select
                                                    name="itemID"
                                                    value={component.itemID}
                                                    label="Inventory Item"
                                                    onChange={(e) => handleComponentChange(index, 'itemID', e.target.value)}
                                                >
                                                    {menuInvtry.map(item => (
                                                        <MenuItem key={item._id} value={item._id}>
                                                            {item.item}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            {/* Remove Button */}
                                            <IconButton color="error" onClick={() => handleRemoveComponent(index)}>
                                                <RemoveCircle />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        onClick={handleAddComponent}
                                        startIcon={<AddCircle />}
                                        variant="text"
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Add Component
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            </Box>
            <Box sx={{ display: "flex", pt: 3, width: '100%', justifyContent: 'flex-start' }}>
                <Button variant="contained" onClick={handleFormSubmit} color="primary" size="large">
                    Save Menu Item
                </Button>
            </Box>
        </Box>
    );
};